/**
 * StageTransition — Animated Overlay (FE-010 / DES-005)
 *
 * Shows a brief (2-second) animated overlay during stage transitions:
 *   Stage 1->2: "Filtered video starting..." with blur-decrease animation
 *   Stage 2->3: "Reveal mode available" with warm glow effect
 *
 * Uses CSS keyframe animations and respects prefers-reduced-motion.
 */

import { type Component, Show } from "solid-js";
import type { StageManager, ActiveTransition } from "../services/stage-manager";
import styles from "./StageTransition.module.css";

export interface StageTransitionProps {
  stageManager: StageManager;
}

const StageTransition: Component<StageTransitionProps> = (props) => {
  const transition = () => props.stageManager.activeTransition();
  const isTransitioning = () => props.stageManager.isTransitioning();

  const isBlurReveal = (t: ActiveTransition | null): boolean =>
    t !== null && t.from === 1 && t.to === 2;

  const isGlowReveal = (t: ActiveTransition | null): boolean =>
    t !== null && t.from === 2 && t.to === 3;

  return (
    <Show when={isTransitioning()}>
      {/* Stage 1 -> 2: Blur Reveal */}
      <Show when={isBlurReveal(transition())}>
        <div
          class={`${styles.overlay} ${styles.overlayBlurReveal}`}
          role="status"
          aria-live="polite"
          aria-label="Transitioning to filtered video"
        >
          <div class={`${styles.content} ${styles.contentBlurReveal}`}>
            <div class={styles.blurCircle} aria-hidden="true" />
            <p class={styles.title}>Filtered video starting</p>
            <p class={styles.subtitle}>You can now see each other with filters</p>
          </div>
        </div>
      </Show>

      {/* Stage 2 -> 3: Glow Reveal */}
      <Show when={isGlowReveal(transition())}>
        <div
          class={`${styles.overlay} ${styles.overlayGlow}`}
          role="status"
          aria-live="polite"
          aria-label="Reveal mode is now available"
        >
          <div class={`${styles.content} ${styles.contentGlow}`}>
            <div class={styles.glowRing} aria-hidden="true" />
            <p class={styles.title}>Reveal mode available</p>
            <p class={styles.subtitle}>You can now choose to show your real self</p>
          </div>
        </div>
      </Show>
    </Show>
  );
};

export default StageTransition;
